import { useState } from 'react';
import type { Ctx } from '../ctx';
import { fmt, TABS, type Tab } from '../model';
import ConfirmDelete from './ConfirmDelete';
import { EnvChip, RegistrationChip, StateLine, VerdictChip } from '../ui';
import PipelineTab from './tabs/PipelineTab';
import MonitorTab from './tabs/MonitorTab';
import LogsTab from './tabs/LogsTab';
import SettingsTab from './tabs/SettingsTab';

export default function WebhookDetail({ ctx, id, tab }: { ctx: Ctx; id: string; tab: Tab }) {
  const [del, setDel] = useState(false);
  const entry = ctx.entries.find((e) => e.id === id);
  if (!entry) {
    return (
      <>
        <StateLine loading={ctx.data.loading} error={ctx.data.error} />
        {!ctx.data.loading ? (
          <div className="empty">
            No webhook <code>{id}</code> on this env.{' '}
            <a
              href="#/webhooks"
              onClick={(e) => {
                e.preventDefault();
                ctx.go({ view: 'webhooks' });
              }}
            >
              Back to the list
            </a>
          </div>
        ) : null}
      </>
    );
  }

  const pipeline = ctx.pipelineFor(entry);
  const crumb = (
    <div className="row" style={{ gap: 8, marginBottom: 2 }}>
      <a
        href="#/webhooks"
        className="dim"
        onClick={(e) => {
          e.preventDefault();
          ctx.go({ view: 'webhooks' });
        }}
      >
        Webhooks
      </a>
      <span className="faint">/</span>
    </div>
  );

  if (!pipeline) {
    return (
      <>
        {crumb}
        <h1>
          {entry.owner === 'zapier' ? 'Zapier catch hook' : 'External intake'}{' '}
          <span className="dim" style={{ fontWeight: 400 }}>
            · {entry.source}
          </span>{' '}
          <EnvChip env={ctx.env} /> <span className="chip">external</span>
        </h1>
        <p className="sub">
          Registered on the same source but delivered to another system — this service never sees
          these calls, so there is no pipeline, monitor or log here.
        </p>
        <section className="card pad">
          <dl className="kv">
            <dt>Webhook id</dt>
            <dd>
              <code>{entry.id}</code>
            </dd>
            <dt>Trigger</dt>
            <dd>
              <code>{entry.trigger}</code>
            </dd>
            <dt>Target</dt>
            <dd className="wrap">
              <code>{entry.target}</code>
            </dd>
            <dt>Owner</dt>
            <dd>{entry.owner === 'zapier' ? 'Zapier' : 'another service'}</dd>
            <dt>Created</dt>
            <dd>{fmt(entry.createdOn)}</dd>
            {entry.note ? (
              <>
                <dt>Note</dt>
                <dd className="wrap">{entry.note}</dd>
              </>
            ) : null}
          </dl>
          <div className="row" style={{ marginTop: 14 }}>
            <button
              type="button"
              className="btn danger"
              disabled={!ctx.admin || entry.registered !== true}
              title={!ctx.admin ? 'admin only' : entry.registered !== true ? 'the source could not be listed' : undefined}
              onClick={() => setDel(true)}
            >
              Delete from {entry.source}
            </button>
            <span className="note">
              Asks for the id typed back; recorded in Tools → Recent actions. This is the cutover step for the zap's hook —
              only after the zap itself is off.
            </span>
          </div>
        </section>
        {del ? <ConfirmDelete ctx={ctx} entry={entry} onClose={() => setDel(false)} /> : null}
      </>
    );
  }

  const dial = entry.pipeline ? ctx.data.dials?.[entry.pipeline] : undefined;

  return (
    <>
      {crumb}
      <h1>
        {pipeline.name}{' '}
        <span className="dim" style={{ fontWeight: 400 }}>
          · {entry.source}
        </span>{' '}
        <EnvChip env={ctx.env} />{' '}
        {pipeline.parked ? <span className="chip warn">parked</span> : null}{' '}
        {entry.registered === true ? null : <RegistrationChip entry={entry} />}{' '}
        <VerdictChip v={ctx.verdict(entry)} />
      </h1>
      <p className="sub">
        <code>
          {entry.registered === true ? `${entry.id} → ` : ''}POST {pipeline.route}
        </code>{' '}
        · {pipeline.auth}
      </p>
      <StateLine loading={ctx.data.loading} error={ctx.data.error} />

      <div className="seg" style={{ marginBottom: 18 }}>
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            className={tab === t ? 'on' : ''}
            onClick={() => ctx.go({ view: 'webhook', id: entry.id, tab: t })}
          >
            {t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'pipeline' && <PipelineTab ctx={ctx} entry={entry} pipeline={pipeline} />}
      {tab === 'monitor' && <MonitorTab ctx={ctx} entry={entry} pipeline={pipeline} />}
      {tab === 'logs' && <LogsTab ctx={ctx} pipeline={pipeline} />}
      {tab === 'settings' && <SettingsTab ctx={ctx} entry={entry} pipeline={pipeline} />}
      {dial && !dial.enabled && tab !== 'pipeline' ? (
        <p className="note">
          This pipeline's dial is off — incoming calls are acknowledged and recorded as{' '}
          <code>skipped:disabled</code>.
        </p>
      ) : null}
    </>
  );
}
